import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Tag, Building, AlertCircle, CheckCircle, TrendingUp, Sparkles, Gift, Frame, Layers, Shield } from "lucide-react";
import { trackCheckoutStart } from "../utils/analytics";
import { API } from "../apiConfig";


const CartPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const isWholesale = user?.role === "wholesale" && user?.wholesale_status === "approved";

  useEffect(() => {
    if (token) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCart = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/cart`, { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      });
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, variantId, newQuantity) => {
    setUpdating(productId + (variantId || ''));
    try {
      if (newQuantity <= 0) {
        await axios.delete(`${API}/cart/remove/${productId}`, { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        });
        toast.success("Item removed from cart");
      } else {
        await axios.put(
          `${API}/cart/update`,
          { product_id: productId, quantity: newQuantity, variant_id: variantId },
          { 
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true 
          }
        );
      }
      await fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update cart");
    } finally {
      setUpdating(null);
    }
  };

  const removeItem = async (productId) => {
    setUpdating(productId);
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      });
      toast.success("Item removed");
      await fetchCart();
    } catch (error) {
      toast.error("Failed to remove item");
    } finally {
      setUpdating(null);
    }
  };

  const handleCheckout = async () => {
    if (!user || !token) {
      toast.info("Please sign in to checkout");
      navigate("/login", { state: { from: { pathname: "/cart" } } });
      return;
    }

    // Validate minimum quantities for wholesale
    if (isWholesale) {
      const invalidItems = cart.items.filter(item => !item.meets_min_qty);
      if (invalidItems.length > 0) {
        toast.error("Some items don't meet minimum wholesale quantity requirements");
        return;
      }
    }

    // Track checkout start
    trackCheckoutStart(cart.total, cart.items?.length || 0);

    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/checkout/create-session`,
        {
          origin_url: window.location.origin,
          payment_method: paymentMethod,
          referral_code: referralCode || null
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.payment_method === "paypal" && response.data.approval_url) {
        // Redirect to PayPal approval URL (includes Pay Later options)
        window.location.href = response.data.approval_url;
      } else if (response.data.payment_method === "paypal") {
        toast.error("PayPal checkout failed. Please try again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-4xl mx-auto animate-pulse">
            <div className="h-10 bg-muted rounded w-1/4 mb-8" />
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="h-32 bg-muted rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const wholesaleEligible = isWholesale && cart.wholesale_eligible;
  const usingWholesalePricing = isWholesale && cart.using_wholesale_pricing;

  // Tier Progress Component
  const TierProgressBar = () => {
    if (!isWholesale || !cart.wholesale_tiers) return null;
    
    const tiers = cart.wholesale_tiers;
    const maxTierValue = tiers[0]?.min_order || 25000; // VIP tier
    const currentValue = cart.retail_total || 0;
    const currentTier = cart.current_tier;
    const nextTier = cart.next_tier;
    
    // Calculate progress percentage (cap at 100%)
    const progressPercent = Math.min((currentValue / maxTierValue) * 100, 100);
    
    // Find which tier checkpoints have been reached
    const getTierPosition = (tierMinOrder) => {
      return (tierMinOrder / maxTierValue) * 100;
    };
    
    // Reverse tiers for display (lowest to highest)
    const sortedTiers = [...tiers].reverse();
    
    return (
      <div className="p-5 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 rounded-2xl border border-primary/20 mb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/20 rounded-full">
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-sm">Wholesale Tier Progress</h3>
              <p className="text-xs text-muted-foreground">
                {currentTier ? `Current: ${currentTier.name}` : 'Build your order to unlock savings'}
              </p>
            </div>
          </div>
          {currentTier && (
            <Badge className="bg-primary text-primary-foreground">
              {(currentTier.discount * 100).toFixed(0)}% OFF
            </Badge>
          )}
        </div>

        {/* Progress Bar */}
        <div className="relative mb-6">
          {/* Track */}
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            {/* Filled Progress */}
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full relative"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" 
                   style={{ backgroundSize: '200% 100%' }} />
            </motion.div>
          </div>
          
          {/* Tier Markers */}
          <div className="absolute top-0 left-0 right-0 h-3 pointer-events-none">
            {sortedTiers.map((tier, index) => {
              const position = getTierPosition(tier.min_order);
              const isReached = currentValue >= tier.min_order;
              const isCurrentTier = currentTier?.min_order === tier.min_order;
              
              return (
                <div 
                  key={tier.name}
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{ left: `${position}%` }}
                >
                  <div className={`w-4 h-4 rounded-full border-2 -ml-2 transition-all ${
                    isReached 
                      ? 'bg-primary border-primary scale-110' 
                      : 'bg-background border-muted-foreground/30'
                  } ${isCurrentTier ? 'ring-2 ring-primary/30 ring-offset-2' : ''}`}>
                    {isReached && (
                      <CheckCircle className="h-3 w-3 text-primary-foreground m-auto mt-0.5" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tier Labels */}
        <div className="flex justify-between text-xs mb-4">
          <span className="text-muted-foreground">$0</span>
          {sortedTiers.map((tier) => (
            <span 
              key={tier.name}
              className={`${currentValue >= tier.min_order ? 'text-primary font-medium' : 'text-muted-foreground'}`}
              style={{ position: 'relative', left: `${getTierPosition(tier.min_order) - 50}%` }}
            >
              ${(tier.min_order / 1000).toFixed(0)}k
            </span>
          ))}
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-3 gap-2">
          {sortedTiers.map((tier) => {
            const isActive = currentTier?.min_order === tier.min_order;
            const isReached = currentValue >= tier.min_order;
            const isNext = nextTier?.min_order === tier.min_order;
            
            return (
              <div 
                key={tier.name}
                className={`p-3 rounded-xl text-center transition-all ${
                  isActive 
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-105' 
                    : isReached
                      ? 'bg-primary/10 border border-primary/30'
                      : 'bg-muted/50 border border-border'
                }`}
              >
                <div className={`text-lg font-bold ${isActive ? '' : isReached ? 'text-primary' : 'text-muted-foreground'}`}>
                  {(tier.discount * 100).toFixed(0)}%
                </div>
                <div className={`text-xs ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {tier.name.split(' ')[0]}
                </div>
                {isNext && (
                  <div className="mt-1 text-xs text-primary font-medium">
                    Next →
                  </div>
                )}
                {isActive && (
                  <Sparkles className="h-3 w-3 mx-auto mt-1" />
                )}
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        {nextTier && cart.amount_to_next_tier > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 bg-primary/10 rounded-xl border border-primary/20"
          >
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-primary shrink-0" />
              <p className="text-sm">
                <span className="text-muted-foreground">Add </span>
                <span className="font-bold text-primary">{formatPrice(cart.amount_to_next_tier)}</span>
                <span className="text-muted-foreground"> more to unlock </span>
                <span className="font-bold text-primary">{nextTier.name}</span>
                <span className="text-muted-foreground"> and save an extra </span>
                <span className="font-bold text-primary">
                  {(((nextTier.discount - (currentTier?.discount || 0)) * 100)).toFixed(0)}%
                </span>
              </p>
            </div>
          </motion.div>
        )}

        {/* Minimum Order Warning */}
        {!currentTier && currentValue > 0 && (
          <div className="mt-4 p-3 bg-muted/50 rounded-xl border border-border">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />
              <p className="text-sm text-muted-foreground">
                Add <span className="font-bold text-foreground">{formatPrice(5000 - currentValue)}</span> more to qualify for <span className="font-bold text-primary">Trade pricing (20% off)</span>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="cart-page">
      <Navigation />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-medium mb-8"
            data-testid="cart-title"
          >
            Your Cart
          </motion.h1>

          {cart.items.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-24"
            >
              <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mx-auto mb-6" />
              <h2 className="text-2xl font-medium mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-8">
                Discover our collection of authentic Aboriginal art
              </p>
              <Link to="/products" data-testid="empty-cart-shop-btn">
                <Button className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
                  Browse Collection
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {/* Wholesale Tier Progress Bar */}
                <TierProgressBar />

                {/* Wholesale Tier Status Banner */}
                {isWholesale && (
                  <div className={`p-4 rounded-xl border ${usingWholesalePricing ? 'bg-primary/5 border-primary/20' : 'bg-muted/50 border-border'}`}>
                    <div className="flex items-center gap-3">
                      {usingWholesalePricing ? (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-muted-foreground" />
                      )}
                      <div className="flex-1">
                        {usingWholesalePricing ? (
                          <>
                            <p className="font-medium text-primary">
                              {cart.current_tier?.name || "Trade"} Pricing Applied — {((cart.current_tier?.discount || 0.2) * 100).toFixed(0)}% Off
                            </p>
                            <p className="text-sm text-muted-foreground">
                              You're saving {formatPrice(cart.wholesale_savings)} on this order
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium">Trade Pricing Not Yet Applied</p>
                            <p className="text-sm text-muted-foreground">
                              {cart.amount_needed_for_wholesale > 0 ? (
                                <>Add {formatPrice(cart.amount_needed_for_wholesale)} more to qualify for trade pricing</>
                              ) : !cart.all_items_meet_min_qty ? (
                                <>Some items don't meet minimum quantity requirements</>
                              ) : null}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Next Tier Indicator */}
                    {usingWholesalePricing && cart.next_tier && cart.amount_to_next_tier > 0 && (
                      <div className="mt-3 pt-3 border-t border-primary/20">
                        <p className="text-sm text-muted-foreground">
                          Add {formatPrice(cart.amount_to_next_tier)} more to unlock{' '}
                          <span className="font-medium text-primary">
                            {cart.next_tier.name} ({(cart.next_tier.discount * 100).toFixed(0)}% off)
                          </span>
                        </p>
                        <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ 
                              width: `${Math.min(100, (cart.retail_total / cart.next_tier.min_order) * 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Tier Legend */}
                    {!usingWholesalePricing && cart.wholesale_tiers && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-2">Trade Discount Tiers:</p>
                        <div className="flex flex-wrap gap-2">
                          {cart.wholesale_tiers.map((tier) => (
                            <span 
                              key={tier.name} 
                              className={`text-xs px-2 py-1 rounded-full ${
                                cart.retail_total >= tier.min_order 
                                  ? 'bg-primary/20 text-primary' 
                                  : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {formatPrice(tier.min_order)}+ = {(tier.discount * 100).toFixed(0)}% off
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {cart.items.map((item, index) => (
                  <motion.div
                    key={item.product_id + (item.variant_id || '') + (item.frame_config?.frame_id || '')}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex gap-6 p-6 bg-card rounded-2xl border ${
                      isWholesale && !item.meets_min_qty ? 'border-destructive/50' : 'border-border'
                    }`}
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Image */}
                    <Link to={`/products/${item.product_id}`} className="shrink-0">
                      <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-muted">
                        <img
                          src={item.product?.images?.[0] || item.image || "https://images.unsplash.com/photo-1663888672547-360e763e4ac7"}
                          alt={item.title || item.product?.title}
                          className="w-full h-full object-contain bg-transparent"
                        />
                      </div>
                    </Link>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${item.product_id}`}>
                        <h3 className="font-medium mb-1 hover:text-primary transition-colors truncate">
                          {item.title || item.product?.title}
                        </h3>
                      </Link>
                      
                      {/* Variant Info */}
                      {(item.variant_name || item.variant?.name) && (
                        <p className="text-sm text-muted-foreground mb-1">
                          Size: {item.variant_name || item.variant?.name} {item.variant_size && `(${item.variant_size})`}
                        </p>
                      )}
                      
                      {/* Frame Configuration Display */}
                      {item.frame_config && item.frame_config.frame_id !== "no_frame" && (
                        <div className="mb-2 p-2 bg-primary/5 rounded-lg border border-primary/10">
                          <div className="flex items-center gap-2 text-sm">
                            <Frame className="h-4 w-4 text-primary" />
                            <span className="font-medium text-primary">
                              {item.frame_config.frame_name}
                            </span>
                            <span className="text-muted-foreground">
                              +{formatPrice(item.frame_config.frame_price)}
                            </span>
                          </div>
                          {item.frame_config.mat_name && item.frame_config.mat_id !== "no_mat" && (
                            <div className="flex items-center gap-2 text-sm mt-1 ml-6">
                              <Layers className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {item.frame_config.mat_name}
                              </span>
                              {item.frame_config.mat_price > 0 && (
                                <span className="text-muted-foreground">
                                  +{formatPrice(item.frame_config.mat_price)}
                                </span>
                              )}
                            </div>
                          )}
                          {item.frame_config.glazing_name && item.frame_config.glazing_price > 0 && (
                            <div className="flex items-center gap-2 text-sm mt-1 ml-6">
                              <span className="text-muted-foreground">
                                {item.frame_config.glazing_name}: +{formatPrice(item.frame_config.glazing_price)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Price Info */}
                      <p className="text-sm text-muted-foreground mb-2">
                        {formatPrice(item.price || item.unit_price)} 
                        {item.frame_config?.total_frame_price > 0 && (
                          <span className="text-primary"> + {formatPrice(item.frame_config.total_frame_price)} framing</span>
                        )}
                        <span className="text-muted-foreground/60"> each</span>
                        {isWholesale && (
                          <span className="text-primary ml-2">
                            (Trade: {formatPrice(item.wholesale_unit_price)})
                          </span>
                        )}
                      </p>

                      {/* Minimum Quantity Warning */}
                      {isWholesale && !item.meets_min_qty && (
                        <p className="text-xs text-destructive flex items-center gap-1 mb-2">
                          <AlertCircle className="h-3 w-3" />
                          Minimum {item.min_wholesale_qty} required for trade pricing
                        </p>
                      )}

                      <div className="flex items-center justify-between">
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity - 1)}
                            disabled={updating === (item.product_id + (item.variant_id || ''))}
                            data-testid={`decrease-qty-${item.product_id}`}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity + 1)}
                            disabled={updating === (item.product_id + (item.variant_id || ''))}
                            data-testid={`increase-qty-${item.product_id}`}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Subtotal & Remove */}
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-medium">
                              {formatPrice(item.item_total || item.subtotal)}
                            </p>
                            {isWholesale && usingWholesalePricing && (
                              <p className="text-sm text-primary">
                                {formatPrice(item.wholesale_subtotal)}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeItem(item.product_id)}
                            disabled={updating === item.product_id}
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Order Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="lg:col-span-1"
              >
                <div className="bg-card rounded-2xl border border-border p-6 sticky top-32">
                  <h2 className="text-xl font-medium mb-6">Order Summary</h2>

                  {/* Referral Code */}
                  <div className="mb-6">
                    <label className="text-sm text-muted-foreground mb-2 block">
                      Referral Code (optional)
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="pl-10 rounded-xl"
                          data-testid="referral-code-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="mb-6">
                    <label className="text-sm text-muted-foreground mb-2 block">
                      Payment Method
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "stripe", label: "Card" },
                        { id: "paypal", label: "PayPal" }
                      ].map((method) => (
                        <button
                          key={method.id}
                          onClick={() => setPaymentMethod(method.id)}
                          className={`py-2 px-3 text-sm rounded-xl border transition-colors ${
                            paymentMethod === method.id
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border hover:border-muted-foreground"
                          }`}
                          data-testid={`payment-method-${method.id}`}
                        >
                          {method.label}
                        </button>
                      ))}
                    </div>
                    
                    {/* PayPal Pay Later Message */}
                    {paymentMethod === "paypal" && (cart.total || cart.retail_total || 0) >= 50 && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                        <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">
                          💳 Pay Later Available
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          {(cart.total || cart.retail_total || 0) <= 1500 
                            ? "Pay in 4 interest-free payments with PayPal"
                            : "Pay Monthly - spread the cost over time"}
                        </p>
                      </div>
                    )}
                  </div>

                  <Separator className="my-6" />

                  {/* Totals */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatPrice(cart.total || cart.retail_total || 0)}</span>
                    </div>
                    
                    {isWholesale && cart.wholesale_total && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Wholesale Subtotal</span>
                          <span className="text-primary">{formatPrice(cart.wholesale_total)}</span>
                        </div>
                        {usingWholesalePricing && cart.wholesale_savings && (
                          <div className="flex justify-between text-sm text-primary">
                            <span>You Save</span>
                            <span>-{formatPrice(cart.wholesale_savings)}</span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      <span>{(cart.total || 0) >= 500 ? "Free" : formatPrice(50)}</span>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="flex justify-between text-lg font-medium mb-2">
                    <span>Total</span>
                    <span data-testid="cart-total">
                      {formatPrice(
                        (usingWholesalePricing ? (cart.wholesale_total || cart.total) : (cart.total || cart.retail_total || 0)) + 
                        ((cart.total || 0) >= 500 ? 0 : 50)
                      )}
                    </span>
                  </div>

                  {isWholesale && !usingWholesalePricing && cart.amount_needed_for_wholesale !== undefined && (
                    <p className="text-xs text-muted-foreground mb-4">
                      {cart.amount_needed_for_wholesale > 0 
                        ? `Add ${formatPrice(cart.amount_needed_for_wholesale)} more for wholesale pricing`
                        : "Meet minimum quantities to unlock wholesale pricing"
                      }
                    </p>
                  )}

                  <Button
                    className="w-full rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 btn-hover"
                    onClick={handleCheckout}
                    disabled={checkingOut || (isWholesale && !cart.all_items_meet_min_qty)}
                    data-testid="checkout-btn"
                  >
                    {checkingOut ? "Processing..." : "Proceed to Checkout"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  {isWholesale && !cart.all_items_meet_min_qty && (
                    <p className="text-xs text-destructive text-center mt-4">
                      Update quantities to meet minimum requirements
                    </p>
                  )}

                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Secure checkout powered by Stripe
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CartPage;
